// Thin axios wrapper. All INPACT endpoints go through this.
import axios, { AxiosError } from "axios";
import type {
  AnamoniBackendResponse,
  FieldsResponse,
  FilterGroup,
  SearchResponse,
  SnapshotStatusResponse,
} from "../types/inpact";
import type {
  AggregationResult,
  AggregationSort,
  ChartType,
} from "../types/chart";

// Shared-secret API key for the interim internal-app guard. Omitting it never
// opens the backend; unauthenticated local use requires INPACT_AUTH_DISABLED=1
// on the backend process.
const _apiKey = import.meta.env.VITE_INPACT_API_KEY as string | undefined;

export const http = axios.create({
  baseURL: "",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    ...(_apiKey ? { "X-API-Key": _apiKey } : {}),
  },
});

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public code: string | null = null,
  ) {
    super(detail);
  }
}

/** What to actually tell the user about a failed request.
 *
 * The explorer's banner used to read "Couldn't reach Salesforce: <message>"
 * for every failure, including the one where the browser never reached the
 * dashboard's own API — which sends whoever reads it to investigate the
 * wrong system entirely. `unwrap` sets `status: 0` precisely when no HTTP
 * response arrived (axios reports that as "Network Error"), so the three
 * cases are distinguishable:
 *
 *  - 0        the API is unreachable from here. Salesforce is not implicated.
 *  - 502/503  the API answered, and what it could not reach was Salesforce.
 *  - anything else: the request itself was refused, and the detail says why
 *    (an unknown filter field, a bad group-by). Blaming a system for those
 *    hides the sentence the user needs to read.
 */
export function describeApiError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : String(error);
  }
  if (error.status === 0) {
    return `Couldn't reach the dashboard API: ${error.detail}. It may be down, or unreachable from this network.`;
  }
  if (error.status === 502 || error.status === 503) {
    return `Couldn't reach Salesforce: ${error.detail}`;
  }
  return error.detail;
}

// Shape of a single FastAPI/Pydantic validation error entry (RFC-ish, not
// guaranteed by any contract — we only ever read `msg` and fall back).
interface ValidationErrorEntry {
  msg?: unknown;
  [key: string]: unknown;
}

/**
 * FastAPI returns a 422's `detail` as either a plain string (our own
 * `HTTPException(detail=...)` calls) or a list of validation-error dicts
 * (raised by a Pydantic `AfterValidator`, e.g. filter_validation.py). The
 * list case must not hit `String()` directly — that stringifies to
 * `"[object Object]"`. Join each entry's `msg`, falling back to a
 * stringified entry when `msg` is missing so this never throws.
 */
export function formatErrorDetail(detail: unknown): string {
  if (Array.isArray(detail)) {
    return detail
      .map((entry) => {
        if (entry && typeof entry === "object") {
          const msg = (entry as ValidationErrorEntry).msg;
          if (typeof msg === "string" && msg) return msg;
        }
        return String(entry);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return String(detail);
}

function errorCode(detail: unknown): string | null {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === "string" && code ? code : null;
}

function unwrap(err: unknown): never {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<{ detail?: unknown }>;
    const detail = ax.response?.data?.detail ?? ax.message;
    throw new ApiError(
      ax.response?.status ?? 0,
      formatErrorDetail(detail),
      errorCode(detail),
    );
  }
  throw err;
}

/**
 * A request made with `responseType: "blob"` (export.csv) gets its error
 * body back as a Blob too — Axios doesn't know to parse it as JSON, so
 * `response.data.detail` is always undefined there and `unwrap` falls back
 * to Axios's generic "Request failed with status code 422". Read the Blob
 * as text and parse it as JSON first; a non-JSON body (a truncated
 * response, an HTML error page from a proxy) must not throw here — fall
 * back to `undefined` so the caller's `?? ax.message` still applies.
 */
async function readBlobErrorDetail(
  blob: Blob,
): Promise<unknown> {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text) as { detail?: unknown };
    return parsed.detail;
  } catch {
    return undefined;
  }
}

async function unwrapBlobError(err: unknown): Promise<never> {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<Blob | { detail?: unknown }>;
    const data = ax.response?.data;
    const detail =
      data instanceof Blob
        ? (await readBlobErrorDetail(data)) ?? ax.message
        : (data as { detail?: unknown } | undefined)?.detail ?? ax.message;
    throw new ApiError(
      ax.response?.status ?? 0,
      formatErrorDetail(detail),
      errorCode(detail),
    );
  }
  throw err;
}

export async function planQuery(
  query: string,
  priorFilterGroup?: FilterGroup,
): Promise<AnamoniBackendResponse> {
  try {
    const { data } = await http.post<AnamoniBackendResponse>(
      "/api/inpact/anamoni",
      { query, prior_filter_group: priorFilterGroup ?? null },
    );
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export interface SearchParams {
  filterGroup: FilterGroup;
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDir?: "ASC" | "DESC";
}

export async function searchContacts(
  params: SearchParams,
): Promise<SearchResponse> {
  try {
    const { data } = await http.post<SearchResponse>("/api/inpact/search", {
      filter_group: params.filterGroup,
      page: params.page ?? 1,
      page_size: params.pageSize ?? 50,
      sort_field: params.sortField ?? "contact.LastName",
      sort_dir: params.sortDir ?? "ASC",
    });
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export interface AggregateRequest {
  filter_group: FilterGroup;
  group_by: string;
  chart_type: ChartType;
  top_n?: number | null;
  sort?: AggregationSort;
}

export async function aggregateContacts(
  body: AggregateRequest,
): Promise<AggregationResult> {
  try {
    const { data } = await http.post<AggregationResult>(
      "/api/inpact/aggregate",
      body,
    );
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

/** Empty FilterGroup helper — single source of truth for "no filters". */
export const EMPTY_FILTER_GROUP: FilterGroup = { logic: "AND", rules: [] };

// ─── CSV export (W1-B-15) ───
export interface ExportCsvParams {
  filterGroup: FilterGroup;
}

export interface ExportCsvResult {
  blob: Blob;
  filename: string;
  truncated: boolean;
  totalMatched: number;
  resultCode: "ok" | "no_matches";
}

function _filenameFromDisposition(header: string | null): string {
  const fallback = "inpact-contacts.csv";
  if (!header) return fallback;
  const m = /filename="?([^";]+)"?/i.exec(header);
  return m?.[1] ?? fallback;
}

export async function exportContactsCsv(
  params: ExportCsvParams,
): Promise<ExportCsvResult> {
  try {
    const resp = await http.post<Blob>(
      "/api/inpact/export.csv",
      { filter_group: params.filterGroup },
      { responseType: "blob" },
    );
    const truncated = String(resp.headers["x-truncated"] ?? "").toLowerCase() === "true";
    const totalMatched = Number(resp.headers["x-total-matched"] ?? 0);
    const resultCode = resp.headers["x-result-code"] === "no_matches"
      ? "no_matches"
      : "ok";
    const filename = _filenameFromDisposition(
      (resp.headers["content-disposition"] as string | undefined) ?? null,
    );
    return { blob: resp.data, filename, truncated, totalMatched, resultCode };
  } catch (err) {
    return unwrapBlobError(err);
  }
}

// ─── Feedback (W1-B-18) ───
export interface FeedbackParams {
  messageId: string;
  rating: "up" | "down";
  note?: string | null;
}

export interface FeedbackResult {
  id: string;
}

export async function submitFeedback(
  params: FeedbackParams,
): Promise<FeedbackResult> {
  try {
    const { data } = await http.post<FeedbackResult>("/api/inpact/feedback", {
      message_id: params.messageId,
      rating: params.rating,
      note: params.note ?? null,
    });
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

// ─── Saved views ───
export interface SavedView {
  id: string;
  name: string;
  filter_group: FilterGroup;
  created_at: string;
  /** Absent only on responses cached from a pre-versioning backend pod. */
  filter_schema_version?: number;
  error: { code: "saved_view_incompatible"; message: string } | null;
}

export async function listSavedViews(): Promise<SavedView[]> {
  try {
    const { data } = await http.get<SavedView[]>("/api/inpact/views");
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export async function createSavedView(
  name: string,
  filterGroup: FilterGroup,
): Promise<SavedView> {
  try {
    const { data } = await http.post<SavedView>("/api/inpact/views", {
      name,
      filter_group: filterGroup,
    });
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export async function deleteSavedView(id: string): Promise<void> {
  try {
    await http.delete(`/api/inpact/views/${encodeURIComponent(id)}`);
  } catch (err) {
    unwrap(err);
  }
}

// ─── Advanced Filter builder field catalogue ───
export async function getFields(): Promise<FieldsResponse> {
  try {
    const { data } = await http.get<FieldsResponse>("/api/inpact/fields");
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

// ─── Authorized snapshot freshness ───
export async function getSnapshotStatus(): Promise<SnapshotStatusResponse> {
  try {
    const { data } = await http.get<SnapshotStatusResponse>("/api/inpact/snapshot");
    return data;
  } catch (err) {
    return unwrap(err);
  }
}

export async function requestSnapshotRefresh(): Promise<{ status: "refresh_started" }> {
  try {
    const { data } = await http.post<{ status: "refresh_started" }>(
      "/api/inpact/snapshot/refresh",
    );
    return data;
  } catch (err) {
    return unwrap(err);
  }
}
